import { DecimalPipe, NgClass } from '@angular/common';
import { Component, computed, input } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { IStackedBarSegment } from './stacked-bars.interface';

/**
 * A single horizontal bar split into proportionally-sized colored segments, with each segment's
 * value labeled above it, a hover tooltip naming the segment, and a legend below mapping each
 * color back to its name. Built as a plain flexbox layout rather than an ECharts chart - for a
 * single bar with this few segments, custom HTML/CSS is simpler than wiring up a charting library.
 */
@Component({
  imports: [DecimalPipe, MatTooltipModule, NgClass, NgxSkeletonLoaderModule],
  selector: 'app-stacked-bars',
  templateUrl: './stacked-bars.component.html',
})
export class StackedBarsComponent {
  public readonly segments = input.required<IStackedBarSegment[]>();
  /** Whether to show placeholder skeletons instead of `segments()` - shown on every load (initial or refetch), not just while `segments()` is still empty. */
  public readonly loading = input<boolean>(false);
  /** How many evenly-sized placeholder segments the loading skeleton shows. */
  public readonly skeletonSegmentCount = input<number>(5);
  /**
   * When `true`, a zero-value segment still renders a small fixed-width sliver instead of
   * collapsing to nothing - off by default so every other existing consumer's layout is unaffected.
   */
  public readonly showZeroBars = input<boolean>(false);

  public readonly skeletonSlots = computed<number[]>(() =>
    Array.from({ length: this.skeletonSegmentCount() }, (_unused, index) => index),
  );

  /** When every segment is 0, none of them can `flex-grow` off their value - falls back to splitting the row evenly instead of leaving most of it blank. */
  private readonly allSegmentsZero = computed<boolean>(
    () => this.segments().length > 0 && this.segments().every((segment) => segment.value === 0),
  );

  /**
   * The CSS `flex` shorthand for a segment - proportional to `value` normally. When
   * `showZeroBars()` is on, a zero-value segment instead gets a fixed small sliver, unless every
   * segment is 0, in which case they all split the row evenly so the bar isn't mostly blank.
   */
  public segmentFlex(segment: IStackedBarSegment): string {
    if (segment.value === 0 && this.showZeroBars()) {
      return this.allSegmentsZero() ? '1 1 0' : '0 0 12px';
    }

    return `${segment.value} 1 0`;
  }
}
